import { ArrowForwardIosOutlined, ArrowBackIosNewOutlined } from '@mui/icons-material'
import { useNavigate, useSearchParams } from 'react-router-dom'
import React, { useEffect, useRef, useState } from 'react'
import { Form, FormikProvider, useFormik } from 'formik'
import FormControl from '@mui/material/FormControl'
import StepLabel from '@mui/material/StepLabel'
import { useTranslation } from 'react-i18next'
import ReCAPTCHA from 'react-google-recaptcha'
import Stepper from '@mui/material/Stepper'
import Stack from '@mui/material/Stack'
import Alert from '@mui/material/Alert'
import Step from '@mui/material/Step'
import Box from '@mui/material/Box'
import CryptoJS from 'crypto-js'

import validatePhoneNumber from 'shared/utils/helpers/validatePhoneNumber'
import ContainedButton from 'shared/components/Buttons/ContainedButton'
import getFingerprint from 'shared/utils/helpers/getFingerprint'
import isAxiosError from 'shared/utils/helpers/isAxiosError'
import { httpClient, verificationRequest } from 'shared/lib'

import { firstStepSchema, secondStepSchema, thirdStepSchema } from './schemas'
import { ValidateSmsCodeResponse, RegisterResponse } from './types'
import { FirstStep, SecondStep, ThirdStep } from './Steps'

export default function RegisterForm() {
   const { t } = useTranslation(['REGISTER', 'GLOBALS'])
   const navigate = useNavigate()
   const [activeStep, setActiveStep] = useState(0)
   const [phoneNumberError, setPhoneNumberError] = useState(false)
   const [searchParams] = useSearchParams()
   const [registerInProgress, setRegisterInProgress] = useState<boolean>(false)
   const [registerErrors, setRegisterErrors] = useState<string[]>([])
   const recaptchaRef = useRef<ReCAPTCHA>(null)
   const schemas = [firstStepSchema, secondStepSchema, thirdStepSchema, {}]
   const [stepLabels, setStepLabels] = useState([
      {
         name: 'userData',
         label: t('REGISTER:stepper.step1'),
      },
      {
         name: 'companyData',
         label: t('REGISTER:stepper.step2'),
      },
      {
         name: 'smsCode',
         label: t('REGISTER:stepper.step3'),
      },
   ])
   const formik = useFormik<RegisterInitialValues>({
      initialValues: {
         email: '',
         firstName: '',
         acceptRegulations: false,
         lastName: '',
         houseNumber: '',
         companyName: '',
         telephoneNumber: '',
         city: '',
         county: '',
         voivodeship: '',
         distributorUuid: null,
         country: 'Poland',
         street: '',
         postCode: '',
         taxID: '',
         workshopEmployees: '0',
         shopEmployees: '0',
         afterSubmit: '',
         smsCode: '',
      },
      validationSchema: schemas[activeStep],
      onSubmit: handleSubmit,
   })

   async function validateSmsCode() {
      const { smsCode } = values
      const { data, status } = await verificationRequest.post<ValidateSmsCodeResponse>('sms/verify', { smsCode })
      if (status === 200) {
         return data.valid
      }
      return false
   }

   const { values, setFieldValue, setFieldError, validateForm, setFieldTouched, isValid, setErrors } = formik

   function handlePhoneNumber(value: string, country: string) {
      setFieldValue('telephoneNumber', value)
      setFieldTouched('telephoneNumber')
      const result = validatePhoneNumber(value, country)
      if (result === false) {
         setPhoneNumberError(true)
      } else {
         setPhoneNumberError(false)
      }
   }

   async function register() {
      const fingerprint = await getFingerprint()
      setRegisterInProgress(true)
      setRegisterErrors([])
      const url = searchParams.get('r') ? `users/register?remarkText=${searchParams.get('r')}` : 'users/register'
      const payload = {
         acceptRegulations: values.acceptRegulations,
         address: {
            city: values.city,
            country: values.country,
            houseNumber: values.houseNumber,
            street: values.street,
            zipCode: values.postCode,
            voivodeship: values.voivodeship,
            county: values.county,
         },
         companyName: values.companyName,
         email: values.email,
         name: values.firstName,
         phoneNumber: values.telephoneNumber,
         surname: values.lastName,
         distributorUuid: values.distributorUuid || null,
         taxID: values.taxID,
         typeService: { shopPositions: values.shopEmployees, workshopPositions: values.workshopEmployees },
      }

      httpClient
         .post<RegisterResponse>(url, payload, { headers: { 'X-Fingerprint': fingerprint } })
         .then((res: any) => {
            if (res.status === 204) {
               navigate('/register-completed')
            }
            navigate('/register-completed')
         })
         .catch((error: any) => {
            if (isAxiosError<ErrorType>(error)) {
               if (Array.isArray(error.response?.data.errors)) {
                  const errors = error.response
                     ? error.response.data.errors.map((mappedError: any) => mappedError.code)
                     : []
                  setRegisterErrors(errors)
               } else {
                  setRegisterErrors([t('apiErrors.500')])
               }
            }
         })
         .finally(() => setRegisterInProgress(false))
   }

   function handleSubmit() {
      setRegisterInProgress(true)
      validateForm().then(async (errors) => {
         const isValidForm = Object.keys(errors).length === 0
         if (isValidForm) {
            const smsCodeIsValid = await validateSmsCode()
            if (smsCodeIsValid) {
               register()
            } else {
               setRegisterInProgress(false)
               if (recaptchaRef.current) {
                  recaptchaRef.current.reset()
               }
               setFieldError('smsCode', 'REGISTER:errors.wrongSmsCode')
            }
         }
      })
   }

   function goStepBack() {
      const lastStep = activeStep - 1
      if (!isValid) {
         setErrors({})
      }
      setRegisterErrors([])
      setFieldValue('smsCode', '')
      setActiveStep(lastStep)
   }

   function goStepNext() {
      validateForm().then((errors) => {
         const isValidForm = Object.keys(errors).length === 0
         if (isValidForm) {
            const nextStep = activeStep + 1
            setActiveStep(nextStep)
         }
      })
   }

   function renderStepContent(step: string) {
      switch (step) {
         case 'userData':
            return <FirstStep handlePhoneNumber={handlePhoneNumber} />
         case 'companyData':
            return <SecondStep />
         case 'smsCode':
            return <ThirdStep recaptchaRef={recaptchaRef} />
         default:
            return <div>{t('GLOBALS:notFound')}</div>
      }
   }

   function renderStepControls(step: string) {
      switch (step) {
         case 'userData':
            return (
               <FormControl sx={{ mt: 1, ml: 'auto', mb: 2 }} variant="standard">
                  <ContainedButton
                     onClick={goStepNext}
                     variant="contained"
                     sx={{ mt: 1, mb: 1 }}
                     disabled={!isValid || phoneNumberError || (activeStep === 2 && !values.country)}
                  >
                     <ArrowForwardIosOutlined sx={{ fontSize: 24 }} />
                  </ContainedButton>
               </FormControl>
            )
         case 'smsCode':
            return (
               <>
                  <FormControl sx={{ mt: 1, ml: 3, mb: 2 }} variant="standard">
                     <ContainedButton onClick={goStepBack} variant="contained" sx={{ mt: 1, mb: 1 }}>
                        <ArrowBackIosNewOutlined sx={{ fontSize: 24 }} />
                     </ContainedButton>
                  </FormControl>
                  <FormControl sx={{ mt: 1, mr: 3, mb: 2 }} variant="standard">
                     <ContainedButton
                        variant="contained"
                        onClick={handleSubmit}
                        sx={{ mt: 1, mb: 1 }}
                        // disabled={!isValid || registerInProgress || !values.acceptRegulations || !values.smsCode}
                     >
                        {t('REGISTER:mainSite.endRegistration')}
                     </ContainedButton>
                  </FormControl>
               </>
            )
         // case 3:
         //    return null
         default:
            return (
               <>
                  <FormControl sx={{ mt: 1, ml: 3, mb: 2 }} variant="standard">
                     <ContainedButton onClick={goStepBack} variant="contained" sx={{ mt: 1, mb: 1 }}>
                        <ArrowBackIosNewOutlined sx={{ fontSize: 24 }} />
                     </ContainedButton>
                  </FormControl>
                  <FormControl sx={{ mt: 1, mr: 3, mb: 2 }} variant="standard">
                     <ContainedButton
                        onClick={goStepNext}
                        variant="contained"
                        sx={{ mt: 1, mb: 1 }}
                        disabled={!isValid}
                     >
                        <ArrowForwardIosOutlined sx={{ fontSize: 24 }} />
                     </ContainedButton>
                  </FormControl>
               </>
            )
      }
   }

   useEffect(() => {
      if (searchParams.get('id')) {
         setFieldValue('distributorUuid', searchParams.get('id'))
      }
   }, [searchParams])

   return (
      <FormikProvider value={formik}>
         <Form>
            <Stepper activeStep={activeStep} sx={{ mb: 2 }}>
               {stepLabels.map(({ label }) => (
                  <Step sx={{ '& .MuiStepLabel-root .MuiStepIcon-text': { fill: 'white' } }} key={label}>
                     <StepLabel sx={{ fontWeight: 'bold' }}>{label}</StepLabel>
                  </Step>
               ))}
            </Stepper>
            <Box sx={{ p: 0, minWidth: { lg: '600px', md: '600px', xs: 'none' } }}>
               {renderStepContent(stepLabels[activeStep].name)}
            </Box>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
               {renderStepControls(stepLabels[activeStep].name)}
            </Stack>
            {registerErrors.length > 0 && (
               <Box sx={{ my: 1 }}>
                  {registerErrors.map((error) => (
                     <Alert key={error} sx={{ mt: 1 }} severity="error">
                        {t(`TRANSLATION:${error}`)}
                     </Alert>
                  ))}
               </Box>
            )}
         </Form>
      </FormikProvider>
   )
}
